/* /step-viewer: the 3D view (three.js r147, vendored).
 *
 * Draws the parts the worker built (step-mesh.js) in their own coordinates —
 * millimetres, Z up, as CAD files are written — so a standard view means what
 * it means in the CAD program the file came from, and the STL export takes
 * the same numbers without a transform.
 *
 * Renders ON DEMAND: a frame when the camera moves, damping settles, the size
 * changes or a part is shown, hidden or picked — never a loop. A big assembly
 * left open in a background tab costs nothing, and on a phone the GPU is not
 * asked for 60 frames a second of the same picture.
 */
(function (global) {
  'use strict'

  var DEFAULT_RGB = [0.72, 0.75, 0.79]
  /* Edges are drawn over light-grey faces in both themes, so they stay dark;
     only "edges only" on the dark background needs light lines. */
  var EDGE_ON_FACES = 0x14171b
  var EDGE_ON_DARK = 0xd5dbe3
  var PICK_RGB = 0xf0a020

  function StepView(host, opts) {
    var THREE = global.THREE
    if (!THREE || !THREE.OrbitControls) throw new Error('three.js is not loaded')
    if (typeof global.__safeWebGL === 'function') global.__safeWebGL.assert()
    opts = opts || {}
    this.THREE = THREE
    this.host = host
    this.dark = !!opts.dark
    this.mode = 'shaded-edges'
    this.parts = []
    this.selected = -1
    this.onPick = opts.onPick || null
    this._frame = 0
    this._mats = {}

    /* The canvas is created before the renderer and guarded the same way as
       /3d-stl-viewer's (3d-printing-core.js makeViewer): three r147 hooks its
       context-restored listener before it has a context, and a context born
       lost would otherwise throw from that listener later. */
    var canvas = document.createElement('canvas')
    var up = false
    canvas.addEventListener('webglcontextrestored', function (ev) { if (!up) ev.stopImmediatePropagation() }, true)
    var renderer
    try {
      renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, preserveDrawingBuffer: false })
    } catch (e) {
      var err = new Error('WebGL context could not be created: ' + ((e && e.message) || e))
      err.code = 'WEBGL_UNAVAILABLE'
      err.expected = true
      throw err
    }
    up = true
    this.renderer = renderer
    renderer.setPixelRatio(Math.min(global.devicePixelRatio || 1, 2))
    canvas.className = 'sv-canvas'
    canvas.setAttribute('tabindex', '0')
    host.appendChild(canvas)
    this.canvas = canvas

    var scene = new THREE.Scene()
    this.scene = scene
    this.root = new THREE.Group()
    scene.add(this.root)
    scene.add(new THREE.HemisphereLight(0xffffff, 0x5a6068, 0.75))
    var cam = new THREE.PerspectiveCamera(30, 1, 0.1, 1000)
    cam.up.set(0, 0, 1)
    this.camera = cam
    /* a head-lamp: the light rides with the camera, so no side of a part is
       ever black whichever way it is turned */
    var lamp = new THREE.DirectionalLight(0xffffff, 0.65)
    lamp.position.set(0.3, 0.4, 1)
    cam.add(lamp)
    scene.add(cam)

    var self = this
    var controls = new THREE.OrbitControls(cam, canvas)
    controls.enableDamping = true
    controls.dampingFactor = 0.12
    controls.screenSpacePanning = true
    controls.addEventListener('change', function () { self.requestRender() })
    this.controls = controls

    this._ro = typeof ResizeObserver === 'function' ? new ResizeObserver(function () { self.resize() }) : null
    if (this._ro) this._ro.observe(host)
    else global.addEventListener('resize', this._onWinResize = function () { self.resize() })

    /* pick: a press that did not move (more than a few pixels) is a click on
       a part; a drag is the orbit */
    var down = null
    canvas.addEventListener('pointerdown', function (e) { down = { x: e.clientX, y: e.clientY, t: Date.now() } })
    canvas.addEventListener('pointerup', function (e) {
      if (!down) return
      var dx = e.clientX - down.x, dy = e.clientY - down.y
      var quick = Date.now() - down.t < 600
      down = null
      if (dx * dx + dy * dy > 36 || !quick) return
      self.pickAt(e.clientX, e.clientY)
    })
    canvas.addEventListener('dblclick', function () { if (self.selected >= 0) self.fit([self.selected]) })

    this.setDark(this.dark)
    this.resize()
  }

  StepView.prototype.requestRender = function () {
    var self = this
    if (this._frame) return
    var raf = global.requestAnimationFrame || function (f) { return setTimeout(f, 16) }
    this._frame = raf(function () {
      self._frame = 0
      var gl = self.renderer.getContext()
      if (gl && gl.isContextLost && gl.isContextLost()) return
      var moving = self.controls.update()
      self._clip()
      self.renderer.render(self.scene, self.camera)
      if (moving) self.requestRender()
    })
  }

  /* near/far follow the distance: an assembly of 2 m with 3-mm screws needs
     a near plane that comes in when the camera does */
  StepView.prototype._clip = function () {
    if (!this._radius) return
    var d = this.camera.position.distanceTo(this.controls.target)
    var r = this._radius
    var near = Math.max(r * 1e-4, (d - r * 1.5) * 0.5, d * 1e-3)
    var far = d + r * 4
    if (Math.abs(near - this.camera.near) / near > 0.05 || Math.abs(far - this.camera.far) / far > 0.05) {
      this.camera.near = near
      this.camera.far = far
      this.camera.updateProjectionMatrix()
    }
  }

  StepView.prototype.resize = function () {
    var w = this.host.clientWidth || 600
    var h = this.host.clientHeight || 400
    this.renderer.setSize(w, h, false)
    this.camera.aspect = w / h
    this.camera.updateProjectionMatrix()
    this.requestRender()
  }

  StepView.prototype.setDark = function (dark) {
    this.dark = !!dark
    this.scene.background = new this.THREE.Color(this.dark ? 0x23262b : 0xeef1f5)
    this._edgeColor()
    this.requestRender()
  }

  StepView.prototype._edgeColor = function () {
    var hex = this.mode === 'edges' && this.dark ? EDGE_ON_DARK : EDGE_ON_FACES
    for (var k in this._edgeMats) this._edgeMats[k].color.setHex(hex)
  }

  StepView.prototype._material = function (rgb) {
    var THREE = this.THREE
    var c = rgb || DEFAULT_RGB
    var key = Math.round(c[0] * 255) + ',' + Math.round(c[1] * 255) + ',' + Math.round(c[2] * 255)
    if (!this._mats[key]) {
      this._mats[key] = new THREE.MeshPhongMaterial({
        color: new THREE.Color(c[0], c[1], c[2]),
        shininess: 28,
        specular: 0x222222,
        side: THREE.DoubleSide,
        polygonOffset: true,
        polygonOffsetFactor: 1,
        polygonOffsetUnits: 1,
      })
    }
    return this._mats[key]
  }

  StepView.prototype.clear = function () {
    for (var i = 0; i < this.parts.length; i++) {
      var p = this.parts[i]
      if (p.mesh) { this.root.remove(p.mesh); p.mesh.geometry.dispose() }
      if (p.lines) { this.root.remove(p.lines); p.lines.geometry.dispose() }
    }
    for (var k in this._mats) this._mats[k].dispose()
    this._mats = {}
    if (this._pickMat) { this._pickMat.dispose(); this._pickMat = null }
    this.parts = []
    this.selected = -1
    this.requestRender()
  }

  /* parts: from the worker — { pos, nrm, idx, groups, palette, edges, bbox, … } */
  StepView.prototype.setParts = function (parts) {
    var THREE = this.THREE
    this.clear()
    this._edgeMats = this._edgeMats || {}
    if (!this._edgeMats.main) this._edgeMats.main = new THREE.LineBasicMaterial({ color: EDGE_ON_FACES })
    for (var i = 0; i < parts.length; i++) {
      var src = parts[i]
      var g = new THREE.BufferGeometry()
      g.setAttribute('position', new THREE.BufferAttribute(src.pos, 3))
      g.setIndex(new THREE.BufferAttribute(src.idx, 1))
      if (src.nrm) g.setAttribute('normal', new THREE.BufferAttribute(src.nrm, 3))
      else g.computeVertexNormals()
      var mats = []
      for (var c = 0; c < src.palette.length; c++) mats.push(this._material(src.palette[c]))
      var gr = src.groups
      for (var j = 0; j < gr.length; j += 3) g.addGroup(gr[j] * 3, gr[j + 1] * 3, gr[j + 2])
      var mesh = new THREE.Mesh(g, mats.length === 1 ? mats[0] : mats)
      if (mats.length === 1) g.clearGroups()
      mesh.userData.part = i
      mesh.userData.mats = mesh.material
      var b = src.bbox
      g.boundingBox = new THREE.Box3(new THREE.Vector3(b[0], b[1], b[2]), new THREE.Vector3(b[3], b[4], b[5]))
      g.boundingSphere = g.boundingBox.getBoundingSphere(new THREE.Sphere())
      this.root.add(mesh)
      var lines = null
      if (src.edges && src.edges.length) {
        var lg = new THREE.BufferGeometry()
        lg.setAttribute('position', new THREE.BufferAttribute(src.edges, 3))
        lines = new THREE.LineSegments(lg, this._edgeMats.main)
        lines.userData.part = i
        this.root.add(lines)
      }
      this.parts.push({ mesh: mesh, lines: lines, src: src, visible: true })
    }
    this.setMode(this.mode)
    var all = this.bounds()
    this._radius = all ? all.getBoundingSphere(new THREE.Sphere()).radius || 1 : 1
    this.view('iso')
  }

  /* 'shaded-edges' | 'shaded' | 'edges' */
  StepView.prototype.setMode = function (mode) {
    this.mode = mode
    for (var i = 0; i < this.parts.length; i++) this._applyVis(i)
    this._edgeColor()
    this.requestRender()
  }

  StepView.prototype._applyVis = function (i) {
    var p = this.parts[i]
    p.mesh.visible = p.visible && this.mode !== 'edges'
    if (p.lines) p.lines.visible = p.visible && this.mode !== 'shaded'
    /* in "edges only" a part without edges (a loose triangulated surface)
       would vanish entirely — keep its faces */
    if (this.mode === 'edges' && !p.lines) p.mesh.visible = p.visible
  }

  StepView.prototype.setVisible = function (indices, on) {
    for (var i = 0; i < indices.length; i++) {
      var p = this.parts[indices[i]]
      if (!p) continue
      p.visible = !!on
      this._applyVis(indices[i])
    }
    this.requestRender()
  }

  StepView.prototype.isolate = function (indices) {
    var keep = {}
    for (var i = 0; i < indices.length; i++) keep[indices[i]] = 1
    for (var j = 0; j < this.parts.length; j++) { this.parts[j].visible = !!keep[j]; this._applyVis(j) }
    this.requestRender()
  }

  StepView.prototype.showAll = function () {
    for (var j = 0; j < this.parts.length; j++) { this.parts[j].visible = true; this._applyVis(j) }
    this.requestRender()
  }

  StepView.prototype.visibleIndices = function () {
    var out = []
    for (var j = 0; j < this.parts.length; j++) if (this.parts[j].visible) out.push(j)
    return out
  }

  StepView.prototype.select = function (i) {
    var THREE = this.THREE
    if (this.selected >= 0 && this.parts[this.selected]) {
      var prev = this.parts[this.selected].mesh
      prev.material = prev.userData.mats
    }
    this.selected = (i >= 0 && this.parts[i]) ? i : -1
    if (this.selected >= 0) {
      if (!this._pickMat) {
        this._pickMat = new THREE.MeshPhongMaterial({
          color: PICK_RGB, emissive: 0x4a2a00, shininess: 40, side: THREE.DoubleSide,
          polygonOffset: true, polygonOffsetFactor: 1, polygonOffsetUnits: 1,
        })
      }
      this.parts[this.selected].mesh.material = this._pickMat
    }
    this.requestRender()
  }

  StepView.prototype.pickAt = function (clientX, clientY) {
    var THREE = this.THREE
    var r = this.canvas.getBoundingClientRect()
    var v = new THREE.Vector2(((clientX - r.left) / r.width) * 2 - 1, -((clientY - r.top) / r.height) * 2 + 1)
    var ray = new THREE.Raycaster()
    ray.setFromCamera(v, this.camera)
    var targets = []
    for (var i = 0; i < this.parts.length; i++) if (this.parts[i].visible) targets.push(this.parts[i].mesh)
    /* in "edges only" faces are hidden but still pickable: the person sees the
       outline of the part they click */
    var hits = ray.intersectObjects(targets, false)
    var idx = hits.length ? hits[0].object.userData.part : -1
    /* a click on empty space clears; a second click on the same part keeps it
       (a double click is two of them, and it means "zoom to this part") */
    if (idx === this.selected) return
    this.select(idx)
    if (this.onPick) this.onPick(this.selected)
  }

  StepView.prototype.bounds = function (indices) {
    var THREE = this.THREE
    var box = new THREE.Box3()
    var list = indices || this.visibleIndices()
    if (!list.length) list = this.parts.map(function (_, i) { return i })
    for (var i = 0; i < list.length; i++) {
      var b = this.parts[list[i]].src.bbox
      box.expandByPoint(new THREE.Vector3(b[0], b[1], b[2]))
      box.expandByPoint(new THREE.Vector3(b[3], b[4], b[5]))
    }
    return box.isEmpty() ? null : box
  }

  var DIRS = {
    iso: [1, -1, 0.8], front: [0, -1, 0], back: [0, 1, 0], top: [0, -0.0001, 1],
    bottom: [0, 0.0001, -1], left: [-1, 0, 0], right: [1, 0, 0],
  }

  StepView.prototype.view = function (name) {
    var d = DIRS[name] || DIRS.iso
    this._fitAlong(new this.THREE.Vector3(d[0], d[1], d[2]).normalize(), this.bounds())
  }

  StepView.prototype.fit = function (indices) {
    var dir = this.camera.position.clone().sub(this.controls.target)
    if (dir.lengthSq() < 1e-12) dir.set(1, -1, 0.8)
    this._fitAlong(dir.normalize(), this.bounds(indices))
  }

  StepView.prototype._fitAlong = function (dir, box) {
    if (!box) return
    var THREE = this.THREE
    var sphere = box.getBoundingSphere(new THREE.Sphere())
    var r = sphere.radius || 1
    var fov = this.camera.fov * Math.PI / 180
    var aspect = this.camera.aspect || 1
    var half = Math.min(fov / 2, Math.atan(Math.tan(fov / 2) * aspect))
    var dist = r / Math.sin(half) * 1.05
    this.controls.target.copy(sphere.center)
    this.camera.position.copy(sphere.center).addScaledVector(dir, dist)
    this.camera.near = Math.max(dist / 1000, r * 1e-4)
    this.camera.far = dist + r * 4
    this.camera.updateProjectionMatrix()
    this.camera.lookAt(sphere.center)
    this.controls.update()
    this.requestRender()
  }

  StepView.prototype.zoom = function (k) {
    var t = this.controls.target
    var off = this.camera.position.clone().sub(t).multiplyScalar(1 / k)
    this.camera.position.copy(t).add(off)
    this.requestRender()
  }

  StepView.prototype.dispose = function () {
    this.clear()
    if (this._ro) this._ro.disconnect()
    if (this._onWinResize) global.removeEventListener('resize', this._onWinResize)
    this.controls.dispose()
    for (var k in this._edgeMats) this._edgeMats[k].dispose()
    this.renderer.dispose()
    try { this.renderer.forceContextLoss() } catch (_) {}
    if (this.canvas.parentNode) this.canvas.parentNode.removeChild(this.canvas)
  }

  global.StepView = StepView
})(typeof window !== 'undefined' ? window : this)
